import { createClient } from '@supabase/supabase-js';

const URL = process.env.REACT_APP_SUPABASE_URL || 'https://tzjlpcsozlngmrpncaef.supabase.co';
const KEY  = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InR6amxwY3NvemxuZ21ycG5jYWVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgzNzkyODcsImV4cCI6MjA5Mzk1NTI4N30.BViS1LgxQ4bcYHj4OO6rKbiPDAl73ukJIexN3duPi1Y';

export const sb = createClient(URL, KEY);

// ── PROFILES ──────────────────────────────────────────────────────────
export async function upsertProfile(data) {
  const { data: row, error } = await sb
    .from('profiles')
    .upsert(data, { onConflict: 'email' })
    .select()
    .single();
  return { row, error };
}

export async function getProfileByEmail(email) {
  const { data, error } = await sb
    .from('profiles')
    .select('*')
    .eq('email', email)
    .single();
  return { data, error };
}

// ── PLANS ─────────────────────────────────────────────────────────────
export async function savePlan(userId, plan) {
  // Mark all previous plans inactive
  await sb.from('plans').update({ is_active: false }).eq('user_id', userId);
  const { data, error } = await sb.from('plans').insert({
    user_id:        userId,
    calories:       plan.macros?.calories,
    protein_g:      plan.macros?.proteinG,
    carbs_g:        plan.macros?.carbG,
    fats_g:         plan.macros?.fatG,
    tdee:           plan.tdee,
    goal:           plan.goal,
    week_num:       1,
    block_name:     'Foundation',
    sessions:       plan.sessions,
    mutation_reason:'initial',
    is_active:      true,
  }).select().single();
  return { data, error };
}

export async function getActivePlan(userId) {
  const { data, error } = await sb
    .from('plans')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  return { data, error };
}

// ── BODY WEIGHT LOGS ──────────────────────────────────────────────────
export async function logWeight(userId, date, kg, notes = '') {
  const { data, error } = await sb
    .from('body_weight_logs')
    .upsert({ user_id: userId, log_date: date, weight_kg: kg, notes },
             { onConflict: 'user_id,log_date' })
    .select().single();
  return { data, error };
}

export async function getWeightLogs(userId, fromDate, toDate) {
  const { data, error } = await sb
    .from('body_weight_logs')
    .select('log_date, weight_kg, notes')
    .eq('user_id', userId)
    .gte('log_date', fromDate)
    .lte('log_date', toDate)
    .order('log_date', { ascending: true });
  return { data: data || [], error };
}

export async function deleteWeightLog(userId, date) {
  const { error } = await sb
    .from('body_weight_logs')
    .delete()
    .eq('user_id', userId)
    .eq('log_date', date);
  return { error };
}

// ── PHOTO UPLOADS ─────────────────────────────────────────────────────
export async function uploadPhoto(userId, date, type, file) {
  const ext  = file.name.split('.').pop() || 'jpg';
  const path = `${userId}/${date}_${type}.${ext}`;

  // Upload to storage
  const { error: uploadError } = await sb.storage
    .from('progress-photos')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (uploadError) return { data: null, error: uploadError };

  // Get public URL
  const { data: { publicUrl } } = sb.storage
    .from('progress-photos')
    .getPublicUrl(path);

  // Save record to DB
  const { data, error } = await sb.from('photo_uploads')
    .upsert({
      user_id:      userId,
      photo_date:   date,
      photo_type:   type,
      storage_path: path,
      public_url:   publicUrl,
      file_size_bytes: file.size,
    }, { onConflict: 'user_id,photo_date,photo_type' })
    .select().single();

  return { data, error };
}

export async function getPhotos(userId) {
  const { data, error } = await sb
    .from('photo_uploads')
    .select('photo_date, photo_type, public_url, storage_path')
    .eq('user_id', userId)
    .order('photo_date', { ascending: false });
  return { data: data || [], error };
}

export async function deletePhoto(userId, date, type) {
  // Get the path first
  const { data: row } = await sb.from('photo_uploads')
    .select('storage_path')
    .eq('user_id', userId)
    .eq('photo_date', date)
    .eq('photo_type', type)
    .single();

  if (row?.storage_path) {
    await sb.storage.from('progress-photos').remove([row.storage_path]);
  }

  const { error } = await sb.from('photo_uploads')
    .delete()
    .eq('user_id', userId)
    .eq('photo_date', date)
    .eq('photo_type', type);

  return { error };
}

// ── WORKOUT SESSIONS ──────────────────────────────────────────────────
export async function startWorkoutSession(userId, planId, session) {
  const { data, error } = await sb.from('workout_sessions').insert({
    user_id:          userId,
    plan_id:          planId,
    session_label:    session.label,
    session_focus:    session.focus,
    session_date:     new Date().toISOString().split('T')[0],
    started_at:       new Date().toISOString(),
    total_exercises:  session.exercises?.length || 0,
    total_sets:       (session.exercises || []).reduce((acc, ex) => acc + (parseInt(ex.sets) || 0), 0),
    completed_sets:   0,
    completion_pct:   0,
  }).select().single();
  return { data, error };
}

export async function finishWorkoutSession(sessionId, stats) {
  const { data, error } = await sb.from('workout_sessions')
    .update({
      finished_at:     new Date().toISOString(),
      duration_minutes:stats.durationMinutes,
      completed_sets:  stats.completedSets,
      total_sets:      stats.totalSets,
      completion_pct:  stats.totalSets > 0
        ? Math.round((stats.completedSets / stats.totalSets) * 100)
        : 0,
      rpe_avg:         stats.rpeAvg || null,
      notes:           stats.notes || null,
    })
    .eq('id', sessionId)
    .select().single();
  return { data, error };
}

export async function logExerciseSets(sessionId, userId, exerciseName, muscle, type, sets) {
  const date = new Date().toISOString().split('T')[0];
  const rows = sets.map((s, i) => ({
    session_id:    sessionId,
    user_id:       userId,
    exercise_name: exerciseName,
    muscle_group:  muscle,
    exercise_type: type,
    set_number:    i + 1,
    target_reps:   s.targetReps,
    actual_reps:   s.actualReps,
    weight_kg:     s.kg ? parseFloat(s.kg) : null,
    rpe:           s.rpe ? parseFloat(s.rpe) : null,
    completed:     s.done,
    log_date:      date,
  }));
  const { data, error } = await sb.from('exercise_logs').insert(rows).select();
  return { data, error };
}

export async function getWorkoutHistory(userId, limit = 20) {
  const { data, error } = await sb
    .from('workout_sessions')
    .select('*, exercise_logs(exercise_name, weight_kg, actual_reps, completed)')
    .eq('user_id', userId)
    .order('session_date', { ascending: false })
    .limit(limit);
  return { data: data || [], error };
}

export async function getExercisePBs(userId) {
  const { data, error } = await sb
    .from('strength_pbs')
    .select('*')
    .eq('user_id', userId);
  return { data: data || [], error };
}
